import React, { useState, useEffect } from "react";
import moment from "moment";
import { FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";

import {
  addContent,
  updateContent,
  getAvailableRooms,
  getAvailableHotels,
  resolveHotelImage,
  uploadImage,
  getPhysicalRooms,
  updatePhysicalRoomStatus,
} from "../../../FIREBASECONFIG";
import {
  fetchHotels,
  fetchReservations,
  invalidateResortCache,
} from "../../../api/resort-booking/resort.service";

import Loader from "../utilities/Loader";

import "react-toastify/dist/ReactToastify.css";
import { validateReservationForm } from "./reservationFormValidation";
import {
  createdHistoryEntry,
  statusChangeHistoryEntry,
} from "./reservationHistory";
import {
  isPhysicalRoomAvailableForStay,
  stayNights,
  buildOccupiedByDate,
  normalizePhysicalRoomAssignments,
} from "./physicalRoomAvailability";

const percentageCal = (percentage, amount) =>
  (Number(percentage) / 100) * Number(amount);

/** Zero charge rates (and tax) when the stay is complimentary. */
const applyRoomCharge = (room, { isComplimentary, taxPercentage }) => {
  const catalogRate = Number(room.rate || 0);
  const qty = Number(room.quantity || 1) || 1;
  const chargeRate = isComplimentary ? 0 : catalogRate;
  const subtotal = chargeRate * qty;
  const tax = isComplimentary ? 0 : percentageCal(taxPercentage, subtotal);

  return {
    ...room,
    chargeRate,
    tax,
    total: subtotal,
    withTax: subtotal + tax,
  };
};

const applyComplimentaryToRooms = (rooms, isComplimentary, taxPercentage) =>
  (rooms || []).map((room) =>
    applyRoomCharge(room, { isComplimentary, taxPercentage })
  );

const nowLocalDateTime = () => moment().format("YYYY-MM-DDTHH:mm");

/** Guest names carry no digits; mobile is 03 + 9 digits; CNIC/passport max 13 digits. */
const FIELD_SANITIZERS = {
  guest: (value) => value.replace(/[^A-Za-z\s'-]/g, ""),
  mobile: (value) => {
    let digits = String(value || "").replace(/\D/g, "");
    if (!digits.startsWith("03")) {
      digits = "03" + digits.replace(/^0?3?/, "");
    }
    return digits.slice(0, 11);
  },
  passport: (value) => String(value || "").replace(/\D/g, "").slice(0, 13),
};

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const d = moment(value);
  return d.isValid() ? d.format("YYYY-MM-DDTHH:mm") : "";
};

const createEmptyPaymentEntry = () => ({
  amount: "",
  mode: "cash",
  bankDetails: "",
  receiptUrl: "",
  remarks: "",
  recordedAt: nowLocalDateTime(),
});

const createInitialPayments = () => ({
  advance: createEmptyPaymentEntry(),
  installment: createEmptyPaymentEntry(),
  final: createEmptyPaymentEntry(),
});

const PAYMENT_TYPE_OPTIONS = [
  { value: "advance", label: "Advance" },
  { value: "installment", label: "Installment" },
  { value: "final", label: "Final Amount" },
];

// Only the website can create this source, so staff cannot pick it manually.
export const TDCP_E_RESERVATION_SOURCE = "tdcp E-Reservation";

export const BOOKING_SOURCE_OPTIONS = [
  { value: "Walk-in", label: "Walk-in" },
  { value: "mobile call", label: "mobile call" },
  { value: "Email", label: "Email" },
  { value: "WhatsApp", label: "WhatsApp" },
  {
    value: TDCP_E_RESERVATION_SOURCE,
    label: TDCP_E_RESERVATION_SOURCE,
    disabled: true,
  },
];

const createDefaultSettlement = () => ({
  enabled: false,
  amount: "",
  mode: "cash",
  receiptUrl: "",
  remarks: "",
  recordedAt: "",
});

const PaymentEntryFields = ({
  entry,
  onChange,
  onReceiptChange,
  existingReceiptUrl,
  disabled = false,
}) => (
  <div className="border rounded p-3 mb-3">
    <div className="row g-3">
      <div className="col-md-3">
        <label className="form-label">Amount</label>
        <input
          type="number"
          min="0"
          className="form-control"
          value={entry.amount}
          onChange={(e) => onChange("amount", e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="col-md-3">
        <label className="form-label">Payment Mode</label>
        <select
          className="form-select"
          value={entry.mode}
          onChange={(e) => onChange("mode", e.target.value)}
          disabled={disabled}
        >
          <option value="cash">Cash</option>
          <option value="bank transfer">Bank Transfer</option>
        </select>
      </div>
      <div className="col-md-3">
        <label className="form-label">Deposit At</label>
        <input
          type="datetime-local"
          className="form-control"
          value={entry.recordedAt || ""}
          onChange={(e) => onChange("recordedAt", e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="col-md-3">
        <label className="form-label">Receipt</label>
        <input
          type="file"
          className="form-control"
          accept="image/*,.pdf"
          onChange={(e) => onReceiptChange(e.target.files?.[0] || null)}
          disabled={disabled}
        />
        {existingReceiptUrl && (
          <a
            href={existingReceiptUrl}
            target="_blank"
            rel="noreferrer"
            className="small d-inline-block mt-1"
          >
            View existing receipt
          </a>
        )}
      </div>
      <div className="col-md-12">
        <label className="form-label">Remarks</label>
        <input
          type="text"
          className="form-control"
          placeholder="Remarks"
          value={entry.remarks || ""}
          onChange={(e) => onChange("remarks", e.target.value)}
          disabled={disabled}
        />
      </div>
    </div>
  </div>
);

const AddReservation = ({
  reservation = null,
  role,
  userDetails,
  toggleForm,
}) => {
  //console.log("r", reservation);

  const initialFormData = {
    reservationType: "Unconfirmed Reservation",
    bookingSource: "Walk-in",
    passport: "",
    guest: "",
    mobile: "03",
    email: "",
    address: "",
    adults: 1,
    children: 0,
    pets: 0,
    infants: 0,
    checkIn: "",
    checkOut: "",
    noOfDays: 1,
    hotelId: "",
    rooms: [],
    totalRate: 0,
    totalRateWithTax: 0,
    bookingNotes: "",
    groupBooking: false,
    isComplimentary: false,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [roomOptions, setRoomOptions] = useState([]);
  const [hotelOptions, setHotelOptions] = useState([]);
  const [physicalRoomCache, setPhysicalRoomCache] = useState({});
  const [occupiedByDate, setOccupiedByDate] = useState(() => new Map());
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);

  const loadPhysicalRoomsForType = async (hotelRoomId, hotelId = formData.hotelId) => {
    if (!hotelRoomId || !hotelId) return [];
    const cacheKey = `${hotelId}:${hotelRoomId}`;
    try {
      // Always refetch so duplicates cleaned by room sync are not stuck in cache
      const units = await getPhysicalRooms(hotelRoomId, hotelId);
      setPhysicalRoomCache((prev) => ({ ...prev, [cacheKey]: units }));
      return units;
    } catch (err) {
      console.error("Error loading physical rooms:", err);
      return [];
    }
  };

  const resizeAssignedPhysicalRooms = (current = [], quantity) => {
    const qty = Math.max(1, Number(quantity) || 1);
    return Array.from({ length: qty }, (_, i) => current[i] || "");
  };

  const getAssignedPhysicalIdsElsewhere = (rooms, rowIdx, slotIdx) => {
    const taken = new Set();
    rooms.forEach((r, i) => {
      (r.assignedPhysicalRooms || []).forEach((id, sIdx) => {
        if (id && !(i === rowIdx && sIdx === slotIdx)) taken.add(id);
      });
    });
    return taken;
  };

  const getCachedPhysicalRooms = (hotelRoomId, hotelId = formData.hotelId) => {
    if (!hotelRoomId || !hotelId) return [];
    return physicalRoomCache[`${hotelId}:${hotelRoomId}`] || [];
  };

  const getPhysicalRoomOptions = (
    hotelRoomId,
    rooms,
    rowIdx,
    slotIdx,
    hotelId = formData.hotelId
  ) => {
    const all = getCachedPhysicalRooms(hotelRoomId, hotelId);
    const taken = getAssignedPhysicalIdsElsewhere(rooms, rowIdx, slotIdx);
    const currentId = rooms[rowIdx]?.assignedPhysicalRooms?.[slotIdx];
    const roomOpt = roomOptions.find((r) => r.id === hotelRoomId);
    const totalRoomCap = Number(roomOpt?.totalRoom) || 0;
    const nights = stayNights(formData.checkIn, formData.checkOut);

    const filtered = all
      .filter((pr) => {
        if (String(pr.hotel_id || "") !== String(hotelId || "")) return false;
        if (String(pr.hotel_room_id || "") !== String(hotelRoomId || "")) {
          return false;
        }
        const isCurrent = pr.id === currentId;
        // Keep the currently selected unit visible when editing
        if (!isCurrent) {
          if (taken.has(pr.id)) return false;
          if (!isPhysicalRoomAvailableForStay(pr, nights, occupiedByDate)) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

    // Collapse duplicate labels (legacy extra docs) — prefer current selection
    const ordered = currentId
      ? [
          ...filtered.filter((p) => p.id === currentId),
          ...filtered.filter((p) => p.id !== currentId),
        ]
      : filtered;
    const seenLabels = new Set();
    const deduped = [];
    for (const pr of ordered) {
      const key = String(pr.label || "").trim().toLowerCase();
      if (!key || seenLabels.has(key)) continue;
      seenLabels.add(key);
      deduped.push(pr);
    }

    // Cap to hotel room quantity — plans share this pool, they do not multiply it
    if (totalRoomCap > 0 && deduped.length > totalRoomCap) {
      const current = deduped.find((p) => p.id === currentId);
      const rest = deduped.filter((p) => p.id !== currentId);
      const capped = current
        ? [current, ...rest].slice(0, totalRoomCap)
        : rest.slice(0, totalRoomCap);
      return capped;
    }

    return deduped;
  };
  const [payments, setPayments] = useState(createInitialPayments);
  const [selectedPaymentType, setSelectedPaymentType] = useState("advance");
  const [receiptFiles, setReceiptFiles] = useState({
    advance: null,
    installment: null,
    final: null,
  });

  // Pre-fill for editing
  useEffect(() => {
    console.log("=====");
    const fetchRooms = async () => {
      // Use formData or reservation values
      const hotelId = formData.hotelId || reservation?.hotelId;
      const checkIn = formData.checkIn || reservation?.checkIn;
      const checkOut = formData.checkOut || reservation?.checkOut;

      if (!hotelId || !checkIn || !checkOut) return;

      setLoadingRooms(true);
      try {
        const rooms = await getAvailableRooms(hotelId, checkIn, checkOut);
        console.log("rooms : ", rooms);
        setRoomOptions(rooms || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchRooms();
  }, [formData.hotelId, formData.checkIn, formData.checkOut, reservation]);

  useEffect(() => {
    const loadPhysicalOccupancy = async () => {
      const hotelId = formData.hotelId || reservation?.hotelId;
      const checkIn = formData.checkIn || reservation?.checkIn;
      const checkOut = formData.checkOut || reservation?.checkOut;
      const nights = stayNights(checkIn, checkOut);
      if (!hotelId || !nights.length) {
        setOccupiedByDate(new Map());
        return;
      }
      try {
        const allReservations = await fetchReservations();
        setOccupiedByDate(
          buildOccupiedByDate(allReservations, {
            hotelId,
            nights,
            excludeReservationId: reservation?.id,
          })
        );
      } catch (err) {
        console.error("Error loading physical room occupancy:", err);
        setOccupiedByDate(new Map());
      }
    };

    loadPhysicalOccupancy();
  }, [
    formData.hotelId,
    formData.checkIn,
    formData.checkOut,
    reservation?.id,
    reservation?.hotelId,
    reservation?.checkIn,
    reservation?.checkOut,
  ]);

  useEffect(() => {
    if (!reservation?.payments) return;

    setPayments({
      advance: {
        ...createEmptyPaymentEntry(),
        ...reservation.payments.advance,
        amount: reservation.payments.advance?.amount ?? "",
        recordedAt: toDateTimeLocal(reservation.payments.advance?.recordedAt),
      },
      installment: {
        ...createEmptyPaymentEntry(),
        ...reservation.payments.installment,
        amount: reservation.payments.installment?.amount ?? "",
        recordedAt: toDateTimeLocal(
          reservation.payments.installment?.recordedAt
        ),
      },
      final: {
        ...createEmptyPaymentEntry(),
        ...reservation.payments.final,
        amount: reservation.payments.final?.amount ?? "",
        recordedAt: toDateTimeLocal(reservation.payments.final?.recordedAt),
      },
    });

    const activeType =
      ["advance", "installment", "final"].find(
        (key) => Number(reservation.payments[key]?.amount || 0) > 0
      ) || "advance";
    setSelectedPaymentType(activeType);
  }, [reservation]);

  useEffect(async () => {
    console.log(reservation, roomOptions);

    if (!reservation) return;
    setFullLoading(true);
    console.log("h=>:", reservation);

    const updatedRooms = (reservation.roomDetails || []).flatMap((r) => {
      const room = roomOptions.find((ro) => ro.id === r.id);
      const ratePlan = room?.plans?.find((ro) => ro.id === r.planId);

      // Fall back to reservation rates when the room/plan is missing from
      // available inventory (common while editing an already-booked stay).
      const nightlyPrices =
        ratePlan?.nightlyPrices ||
        (r.rate || []).map((x) => ({
          date: x.date,
          price: Number(x.ratePerNight ?? x.price ?? 0),
        }));

      const unitCount = Math.max(
        Number(r.quantity || 1),
        normalizePhysicalRoomAssignments(r).length,
        1,
      );
      const totalCharge = Number(
        r.roomTotalPrice ??
          r.rate?.reduce((sum, x) => sum + Number(x.ratePerNight), 0) ??
          0,
      );
      const totalTax =
        r.rate?.reduce((sum, x) => sum + Number(x.taxAmount), 0) || 0;
      const unitCharge = totalCharge / unitCount;
      const unitTax = totalTax / unitCount;
      const catalogRate =
        r.rate?.reduce((sum, x) => sum + Number(x.ratePerNight), 0) || 0;

      // One form row per physical room (quantity is always 1 per row).
      return Array.from({ length: unitCount }, (_, i) => {
        const pr = normalizePhysicalRoomAssignments(r)[i];
        return {
          roomId: r.id,
          name: r.name,
          ratePlanId: r.planId,
          ratePlan: r.planName,
          quantity: 1,
          rate: catalogRate / unitCount || catalogRate,
          chargeRate: unitCharge,
          tax: unitTax,
          total: unitCharge,
          withTax: unitCharge + unitTax,
          availablePlans: room?.plans || [],
          nightlyPrices,
          guests: r.guests || reservation.adults,
          assignedPhysicalRooms: [pr?.id || ""],
        };
      });
    });

    // Prefetch physical room units for each room type
    await Promise.all(
      [...new Set(updatedRooms.map((r) => r.roomId).filter(Boolean))].map(
        (id) => loadPhysicalRoomsForType(id, reservation.hotelId || formData.hotelId)
      )
    );

    setFormData((prev) => ({
      ...prev,
      ...reservation,
      bookingSource: reservation.source || prev.bookingSource,
      guest: reservation.firstName + " " + reservation.lastName,
      mobile: FIELD_SANITIZERS.mobile(reservation.mobile || "03"),
      passport: FIELD_SANITIZERS.passport(reservation.passport || ""),
      pets: reservation.pets ?? 0,
      infants: reservation.infants ?? 0,
      isComplimentary: Boolean(reservation.isComplimentary),
      rooms: updatedRooms,
      totalRate: updatedRooms.reduce((a, r) => a + r.total, 0),
      totalRateWithTax: updatedRooms.reduce((a, r) => a + r.withTax, 0),
    }));

    setFullLoading(false);
  }, [reservation, roomOptions]);

  // Load all approved hotels on mount (dropdown always has options)
  useEffect(() => {
    const loadHotels = async () => {
      try {
        const all = await fetchHotels();
        let Hotels = (all || []).filter((h) => h.isApproved === true);
        if (role === "hoteladmin" && userDetails?.hotelId) {
          Hotels = Hotels.filter((h) => h.id === userDetails.hotelId);
        }
        setHotelOptions(
          Hotels.map((h) => ({
            label: h.name,
            value: h.id,
            rawData: h,
          }))
        );
      } catch (err) {
        console.error(err);
      }
    };
    loadHotels();
  }, [role, userDetails?.hotelId]);

  // Handle general input change
  const isEditingReservation = Boolean(reservation?.id);
  const todayYMD = moment().format("YYYY-MM-DD");
  const handleChange = (e) => {
    const { name } = e.target;
    const sanitize = FIELD_SANITIZERS[name];
    const value = sanitize ? sanitize(e.target.value) : e.target.value;

    // Check-in is locked while editing an existing reservation
    if (isEditingReservation && name === "checkIn") return;

    if (name === "hotelId") {
      setPhysicalRoomCache({});
    }

    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      const isNewReservation = !isEditingReservation;

      // Changing hotel/dates invalidates selected rooms for new bookings
      if (
        isNewReservation &&
        (name === "hotelId" || name === "checkIn" || name === "checkOut")
      ) {
        next.rooms = [];
        next.totalRate = 0;
        next.totalRateWithTax = 0;
      }

      const inDate = moment(next.checkIn, "YYYY-MM-DD", true);
      const outDate = moment(next.checkOut, "YYYY-MM-DD", true);
      const today = moment(todayYMD, "YYYY-MM-DD", true).startOf("day");

      // Prevent selecting past dates (new reservations only)
      if (isNewReservation) {
        if (name === "checkIn" && next.checkIn && inDate.isValid()) {
          if (!inDate.isSameOrAfter(today, "day")) {
            toast.error("Check-in date cannot be in the past.");
            next.checkIn = "";
            next.checkOut = "";
            return next;
          }
        }

        if (name === "checkOut" && next.checkOut && outDate.isValid()) {
          if (!outDate.isSameOrAfter(today, "day")) {
            toast.error("Check-out date cannot be in the past.");
            next.checkOut = "";
            return next;
          }
        }
      }

      // Edit mode: check-out may only move to a future date (today or later)
      if (
        isEditingReservation &&
        name === "checkOut" &&
        next.checkOut &&
        outDate.isValid() &&
        !outDate.isSameOrAfter(today, "day")
      ) {
        toast.error("Check-out date can only be changed to a future date.");
        next.checkOut = prev.checkOut;
        return next;
      }

      // Enforce: check-out must be strictly greater than check-in
      if (
        next.checkIn &&
        next.checkOut &&
        inDate.isValid() &&
        outDate.isValid() &&
        !outDate.isAfter(inDate, "day")
      ) {
        toast.warning("Check-out must be greater than check-in.");
        next.checkOut = "";
      }

      return next;
    });
  };

  const getSelectedHotelTaxPercentage = (hotelId = formData.hotelId) => {
    const selectedHotel = hotelOptions.find((h) => h.value === hotelId);
    return selectedHotel?.rawData?.taxes?.[0]?.percentage || 0;
  };

  const handleComplimentaryChange = (e) => {
    const isComplimentary = e.target.checked;
    const taxPercentage = getSelectedHotelTaxPercentage();
    const rooms = applyComplimentaryToRooms(
      formData.rooms,
      isComplimentary,
      taxPercentage
    );

    setFormData((prev) => ({
      ...prev,
      isComplimentary,
      rooms,
      totalRate: rooms.reduce((a, r) => a + Number(r.total || 0), 0),
      totalRateWithTax: rooms.reduce((a, r) => a + Number(r.withTax || 0), 0),
    }));
  };

  // Build a room row defaulting to first room type + first plan
  const buildDefaultRoomRow = (options = roomOptions, hotelId = formData.hotelId) => {
    const firstRoom = options[0];
    if (!firstRoom) return null;

    const firstPlan = firstRoom.plans?.[0] || null;
    const quantity = 1;
    const rate = Number(firstPlan?.price || 0);
    const taxPercentage = getSelectedHotelTaxPercentage(hotelId);

    return applyRoomCharge(
      {
        roomId: firstRoom.id,
        name: firstRoom.name,
        availablePlans: firstRoom.plans || [],
        ratePlanId: firstPlan?.id || "",
        ratePlan: firstPlan?.name || "",
        chargeType: "0",
        rate,
        quantity,
        nightlyPrices: firstPlan?.nightlyPrices || [],
        assignedPhysicalRooms: [""],
      },
      { isComplimentary: formData.isComplimentary, taxPercentage }
    );
  };

  // Auto-add first room row when hotel + dates load available rooms (new bookings only)
  useEffect(() => {
    if (isEditingReservation) return;
    if (!formData.hotelId || !formData.checkIn || !formData.checkOut) return;
    if (!roomOptions.length) return;
    if (formData.rooms.length > 0) return;

    const newRoom = buildDefaultRoomRow(roomOptions, formData.hotelId);
    if (!newRoom) return;

    if (newRoom.roomId) loadPhysicalRoomsForType(newRoom.roomId);

    setFormData((prev) => {
      if (prev.rooms.length > 0) return prev;
      return {
        ...prev,
        rooms: [newRoom],
        totalRate: newRoom.total,
        totalRateWithTax: newRoom.withTax,
      };
    });
  }, [roomOptions, formData.hotelId, formData.checkIn, formData.checkOut, isEditingReservation]);

  // Add room — defaults to first available room type + first plan
  const handleAddRoom = () => {
    if (!formData.hotelId) {
      toast.warning("Please select a hotel first!");
      return;
    }
    if (!roomOptions.length) {
      toast.warning("No rooms available for the selected dates.");
      return;
    }

    const newRoom = buildDefaultRoomRow();
    if (!newRoom) return;

    if (newRoom.roomId) loadPhysicalRoomsForType(newRoom.roomId);

    setFormData((prev) => {
      const rooms = [...prev.rooms, newRoom];
      return {
        ...prev,
        rooms,
        totalRate: rooms.reduce((a, r) => a + (r.total || 0), 0),
        totalRateWithTax: rooms.reduce((a, r) => a + (r.withTax || 0), 0),
      };
    });
  };

  // Availability comes from getAvailableRooms:
  // plan.availableQuantity = plan.totalPlan - reserved qty
  // (only Confirmed / Check-in reservations block inventory)
  const getPlanBaseAvailability = (roomId, planId) => {
    if (!roomId || !planId) return null;
    const roomOpt = roomOptions.find((r) => r.id === roomId);
    const plan = roomOpt?.plans?.find((p) => p.id === planId);
    if (!plan) return null;

    // Exact field returned by getAvailableRooms (based on plan.totalPlan)
    let available = Number(plan.availableQuantity || 0);

    // When editing, getAvailableRooms already counted this reservation's
    // roomDetails.quantity — add that qty back for the same id + planId
    if (reservation?.roomDetails?.length) {
      available += reservation.roomDetails
        .filter((detail) => detail.id === roomId && detail.planId === planId)
        .reduce((sum, detail) => sum + Number(detail.quantity || 0), 0);
    }

    return Math.max(0, available);
  };

  const getMaxQuantityForRoom = (rooms, idx) => {
    const room = rooms[idx];
    if (!room?.roomId || !room?.ratePlanId) return null;

    const baseAvailable = getPlanBaseAvailability(
      room.roomId,
      room.ratePlanId
    );
    if (baseAvailable == null) return null;

    // Other form rows booking the same roomId + ratePlanId also consume qty
    const usedByOtherRows = rooms.reduce((sum, r, i) => {
      if (i === idx) return sum;
      if (r.roomId === room.roomId && r.ratePlanId === room.ratePlanId) {
        return sum + Number(r.quantity || 0);
      }
      return sum;
    }, 0);

    return Math.max(0, baseAvailable - usedByOtherRows);
  };

  const handleRoomChange = (idx, field, value) => {
    console.log(formData);
    const updated = [...formData.rooms];
    if (field !== "assignedPhysicalRoom") {
      updated[idx][field] = value;
    }

    // If room type is selected → load its plans + physical rooms, default first plan
    if (field === "roomId") {
      const selectedRoom = roomOptions.find((r) => r.id === value);
      console.log("Room", selectedRoom);
      if (selectedRoom) {
        const firstPlan = selectedRoom.plans?.[0] || null;
        const rate = Number(firstPlan?.price || 0);
        const qty = 1;
        const taxPercentage = getSelectedHotelTaxPercentage();
        const charged = applyRoomCharge(
          {
            ...updated[idx],
            availablePlans: selectedRoom.plans || [],
            ratePlanId: firstPlan?.id || "",
            name: selectedRoom.name,
            ratePlan: firstPlan?.name || "",
            rate,
            quantity: qty,
            nightlyPrices: firstPlan?.nightlyPrices || [],
            assignedPhysicalRooms: [""],
          },
          { isComplimentary: formData.isComplimentary, taxPercentage }
        );
        updated[idx] = charged;
        loadPhysicalRoomsForType(value);
      }
    }

    if (field === "assignedPhysicalRoom") {
      // value shape: { slotIdx, physicalRoomId } — each room row has one slot
      const { slotIdx = 0, physicalRoomId } = value;
      const assigned = [...(updated[idx].assignedPhysicalRooms || [""])];
      assigned[slotIdx] = physicalRoomId;
      updated[idx].assignedPhysicalRooms = assigned;
      updated[idx].quantity = 1;
    }

    if (field === "chargeRate") {
      if (formData.isComplimentary) {
        updated[idx].chargeRate = 0;
        updated[idx].quantity = 1;
        updated[idx].total = 0;
        updated[idx].tax = 0;
        updated[idx].withTax = 0;
      } else {
        const rate = Number(
          updated[idx].chargeRate !== "" && updated[idx].chargeRate != null
            ? updated[idx].chargeRate
            : updated[idx].rate || 0
        );
        const qty = 1;
        updated[idx].quantity = qty;
        const subtotal = rate * qty;
        const taxPercentage = getSelectedHotelTaxPercentage();
        const tax = percentageCal(taxPercentage, subtotal);

        updated[idx].total = subtotal;
        updated[idx].tax = tax;
        updated[idx].withTax = subtotal + tax;
      }
    }

    setFormData((prev) => ({
      ...prev,
      rooms: updated,
      totalRate: updated.reduce((a, r) => a + r.total, 0),
      totalRateWithTax: updated.reduce((a, r) => a + r.withTax, 0),
    }));
  };

  const handlePlanSelect = (idx, planId) => {
    const updated = [...formData.rooms];
    const selectedRoom = roomOptions.find((r) => r.id === updated[idx].roomId);
    if (!selectedRoom) return;

    const plan = selectedRoom.plans.find((p) => p.id === planId);
    if (!plan) return;

    updated[idx].ratePlanId = plan.id;
    updated[idx].ratePlan = plan.name;
    updated[idx].rate = Number(plan.price);
    updated[idx].nightlyPrices = plan.nightlyPrices || [];
    updated[idx].quantity = 1;
    updated[idx].assignedPhysicalRooms = resizeAssignedPhysicalRooms(
      updated[idx].assignedPhysicalRooms,
      1
    );

    const maxQty = getMaxQuantityForRoom(updated, idx);
    if (maxQty != null && maxQty < 1) {
      toast.warning("No rooms available for this plan.");
    }
    loadPhysicalRoomsForType(updated[idx].roomId);

    const taxPercentage = getSelectedHotelTaxPercentage();
    updated[idx] = applyRoomCharge(updated[idx], {
      isComplimentary: formData.isComplimentary,
      taxPercentage,
    });

    setFormData((prev) => ({
      ...prev,
      rooms: updated,
      totalRate: updated.reduce((a, r) => a + r.total, 0),
      totalRateWithTax: updated.reduce((a, r) => a + r.withTax, 0),
    }));
  };

  const handleDeleteRoom = (idx) => {
    if (formData.rooms.length <= 1) {
      toast.warning("A reservation must have at least one room.");
      return;
    }

    const updated = [...formData.rooms];
    updated.splice(idx, 1);
    setFormData((prev) => ({
      ...prev,
      rooms: updated,
      totalRate: updated.reduce((a, r) => a + r.total, 0),
      totalRateWithTax: updated.reduce((a, r) => a + r.withTax, 0),
    }));
  };

  // Calculate number of days
  useEffect(() => {
    if (formData.checkIn && formData.checkOut) {
      const diff =
        (new Date(formData.checkOut) - new Date(formData.checkIn)) /
        (1000 * 60 * 60 * 24);
      setFormData((prev) => ({ ...prev, noOfDays: diff > 0 ? diff : 1 }));
    }
  }, [formData.checkIn, formData.checkOut]);

  const grandTotal = Number(formData.totalRateWithTax || 0);
  const checkInMin = isEditingReservation ? undefined : todayYMD;
  const parsedCheckIn = moment(formData.checkIn, "YYYY-MM-DD", true);
  const dayAfterCheckIn = parsedCheckIn.isValid()
    ? parsedCheckIn.clone().add(1, "day")
    : null;
  // Edit mode: checkout must be today or later (and still after check-in)
  const checkOutMin = (() => {
    if (isEditingReservation) {
      const today = moment(todayYMD, "YYYY-MM-DD", true);
      if (dayAfterCheckIn) {
        return moment.max(dayAfterCheckIn, today).format("YYYY-MM-DD");
      }
      return todayYMD;
    }
    if (dayAfterCheckIn) return dayAfterCheckIn.format("YYYY-MM-DD");
    return todayYMD;
  })();

  const handlePaymentChange = (section, field, value) => {
    if (isEditingReservation) return;
    setPayments((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const handleReceiptChange = (section, file) => {
    if (isEditingReservation) return;
    setReceiptFiles((prev) => ({ ...prev, [section]: file }));
  };

  const uploadReceipt = async (file, existingUrl = "") => {
    if (!file) return existingUrl || "";
    return uploadImage(file, "payment_receipts");
  };

  const buildPaymentPayload = async () => {
    const [advanceReceipt, installmentReceipt, finalReceipt] = await Promise.all([
      uploadReceipt(receiptFiles.advance, payments.advance.receiptUrl),
      uploadReceipt(receiptFiles.installment, payments.installment.receiptUrl),
      uploadReceipt(receiptFiles.final, payments.final.receiptUrl),
    ]);

    const preservedSettlement =
      reservation?.payments?.settlement || createDefaultSettlement();

    const stampPayment = (entry, receiptUrl) => {
      const amount = Number(entry.amount || 0);

      return {
        ...entry,
        amount,
        receiptUrl,
        recordedAt:
          amount > 0 ? entry.recordedAt || nowLocalDateTime() : "",
      };
    };

    const normalizedPayments = {
      advance: stampPayment(payments.advance, advanceReceipt),
      installment: stampPayment(payments.installment, installmentReceipt),
      final: stampPayment(payments.final, finalReceipt),
      settlement: {
        ...preservedSettlement,
        amount: preservedSettlement.enabled
          ? Number(preservedSettlement.amount || 0)
          : 0,
        recordedAt: preservedSettlement.recordedAt || "",
      },
    };

    const paidTotal =
      normalizedPayments.advance.amount +
      normalizedPayments.installment.amount +
      normalizedPayments.final.amount;

    const settlement = normalizedPayments.settlement.enabled
      ? normalizedPayments.settlement.amount
      : 0;

    const net = Math.max(grandTotal - settlement, 0);

    return {
      payments: normalizedPayments,
      paymentSummary: {
        grandTotal,
        settlement,
        netPayable: net,
        totalPaid: paidTotal,
        balanceDue: net - paidTotal,
      },
    };
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateReservationForm({
      formData,
    });
    if (!validation.ok) {
      toast.error(validation.message);
      return;
    }

    const isNewReservation = !isEditingReservation;

    const inDate = moment(formData.checkIn, "YYYY-MM-DD", true);
    const outDate = moment(formData.checkOut, "YYYY-MM-DD", true);

    if (!inDate.isValid() || !outDate.isValid()) {
      toast.error("Please select valid check-in and check-out dates.");
      return;
    }

    const today = moment(todayYMD, "YYYY-MM-DD", true).startOf("day");

    // Past-date restriction (new reservations only)
    if (isNewReservation) {
      if (!inDate.isSameOrAfter(today, "day")) {
        toast.error("Check-in date cannot be in the past.");
        return;
      }
      if (!outDate.isSameOrAfter(today, "day")) {
        toast.error("Check-out date cannot be in the past.");
        return;
      }
    }

    // Edit mode: check-out may only be today or a future date
    if (isEditingReservation && !outDate.isSameOrAfter(today, "day")) {
      toast.error("Check-out date can only be changed to a future date.");
      return;
    }

    // Strictly greater: checkout > checkin
    if (!outDate.isAfter(inDate, "day")) {
      toast.error("Check-out date must be greater than check-in date.");
      return;
    }

    for (let i = 0; i < formData.rooms.length; i++) {
      const room = formData.rooms[i];
      const maxQty = getMaxQuantityForRoom(formData.rooms, i);
      if (maxQty != null && maxQty < 1) {
        toast.error(
          `No availability left for ${room.name || "selected room"} / ${room.ratePlan || "plan"}.`
        );
        return;
      }
    }

    const selectedHotel = hotelOptions.find(
      (h) => h.value === formData.hotelId
    )?.rawData;

    setFullLoading(true);

    try {
      const physicalUnitsByType = {};
      await Promise.all(
        [...new Set(formData.rooms.map((r) => r.roomId).filter(Boolean))].map(
          async (id) => {
            physicalUnitsByType[id] = await loadPhysicalRoomsForType(
              id,
              formData.hotelId
            );
          }
        )
      );

      const { payments: paymentPayload, paymentSummary } =
        await buildPaymentPayload();

    // 🔹 Prepare roomDetails in correct format
    console.log("formData", formData);
    const roomDetails = formData.rooms.map((r) => {
      const originalNightly = r.nightlyPrices || [];
      const originalTotal =
        originalNightly.reduce((sum, n) => sum + Number(n.price || 0), 0) ||
        Number(r.rate || 0);
      const chargeTotal = formData.isComplimentary
        ? 0
        : Number(
            r.chargeRate !== "" && r.chargeRate != null
              ? r.chargeRate
              : r.rate || 0
          );
      const roomTax = formData.isComplimentary ? 0 : Number(r.tax || 0);

      const selectedPricingArray = originalNightly.length
        ? originalNightly.map((n) => {
            const basePrice = Number(n.price || 0);
            const ratePerNight =
              originalTotal > 0
                ? (basePrice / originalTotal) * chargeTotal
                : chargeTotal / originalNightly.length;
            return {
              date: n.date,
              ratePerNight,
              taxAmount:
                chargeTotal > 0
                  ? (ratePerNight * roomTax) / chargeTotal
                  : 0,
            };
          })
        : [
            {
              date: formData.checkIn,
              ratePerNight: chargeTotal,
              taxAmount: roomTax,
            },
          ];

      console.log("selectedPricingArray", selectedPricingArray);
      const cachedUnits = physicalUnitsByType[r.roomId] || [];
      const physicalRooms = (r.assignedPhysicalRooms || [])
        .filter(Boolean)
        .map((id) => {
          const unit = cachedUnits.find((u) => u.id === id);
          const previous = normalizePhysicalRoomAssignments(
            (reservation?.roomDetails || []).find((d) => d.id === r.roomId) || {}
          ).find((p) => p.id === id);
          return {
            id,
            label: unit?.label || previous?.label || "",
          };
        });

      return {
        guests: Number(r.guests || formData.adults),
        id: r.roomId,
        name: r.name || "",
        planId: r.ratePlanId,
        planName: r.ratePlan,
        quantity: 1,
        physicalRooms,
        rate: selectedPricingArray,
        originalRate: Number(r.rate || 0),
        chargeRate: chargeTotal,
        roomTotalPrice: selectedPricingArray.reduce(
          (sum, a) => sum + Number(a.ratePerNight),
          0
        ),
        roomTotalTax: selectedPricingArray.reduce(
          (sum, a) => sum + Number(a.taxAmount),
          0
        ),
      };
    });

    // 🔹 Prepare final reservation object
    const newReservation = {
      address: formData.address,
      bookingDate: moment().format("YYYY-MM-D"),
      bookingId: new Date().getTime().toString(),
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      noOfDays: formData.noOfDays,
      adults: Number(formData.adults),
      children: Number(formData.children || 0),
      pets: Number(formData.pets || 0),
      infants: Number(formData.infants || 0),
      checkInTime: formData.checkInTime || "",
      checkOutTime: formData.checkOutTime || "",
      city: selectedHotel?.city || "",
      isAscendant: false,
      isComplimentary: Boolean(formData.isComplimentary),
      discount: formData.discount || 0,
      bookingNotes: formData.bookingNotes || "",
      discountType: formData.discountType || "",
      source: formData.bookingSource,
      email: formData.email || "",
      employeeId: "",
      firstName: formData.guest.split(" ")[0] || "",
      hotelName: selectedHotel.name,
      hotelId: selectedHotel?.id || "",
      id: reservation?.id || new Date().getTime().toString(),
      image: resolveHotelImage(selectedHotel),
      lastName: formData.guest.split(" ")[1] || "",
      mobile: formData.mobile,
      name: selectedHotel?.name || "",
      roomDetails: roomDetails,
      status: "pending",
      reservationType: formData.reservationType,
      color: formData.color || "", // optional
      taxes: formData.isComplimentary
        ? 0
        : Number(formData.totalRateWithTax || 0) -
          Number(formData.totalRate || 0),
      totalPrice: formData.isComplimentary
        ? 0
        : Number(formData.totalRate || 0),
      userId: "",
      payProId: "",
      pinCode: new Date().getTime().toString(),
      passport: formData.passport || "",
      payments: paymentPayload,
      paymentSummary,
    };
    console.log("new reservation:", newReservation);

      // Sync physical room statuses (release old, occupy new)
      const previousPhysicalIds = (reservation?.roomDetails || []).flatMap(
        (d) => (d.physicalRooms || []).map((p) => p.id || p).filter(Boolean)
      );
      const nextPhysicalIds = roomDetails.flatMap((d) =>
        (d.physicalRooms || []).map((p) => p.id).filter(Boolean)
      );
      const nextSet = new Set(nextPhysicalIds);

      await Promise.all([
        ...previousPhysicalIds
          .filter((id) => !nextSet.has(id))
          .map((id) => updatePhysicalRoomStatus(id, "available")),
        ...nextPhysicalIds.map((id) =>
          updatePhysicalRoomStatus(id, "occupied")
        ),
      ]);

      if (reservation) {
        // The status dropdown lives in this form too, so it is audited here as well.
        const statusEntry =
          reservation.reservationType !== formData.reservationType
            ? statusChangeHistoryEntry({
                from: reservation.reservationType,
                to: formData.reservationType,
              })
            : null;

        await updateContent(
          "reservations",
          "",
          reservation.id,
          statusEntry
            ? {
                ...newReservation,
                history: [...(reservation.history || []), statusEntry],
              }
            : newReservation,
          null
        );
        toast.success("Reservation updated!");
        toggleForm();
      } else {
        await addContent(
          "reservations",
          "",
          {
            ...newReservation,
            history: [
              createdHistoryEntry({
                status: formData.reservationType,
                source: formData.bookingSource,
              }),
            ],
          },
          null
        );
        toast.success("Reservation added!");
      }
      invalidateResortCache.afterReservationChange();
      setFormData(initialFormData);
      setPayments(createInitialPayments());
      setSelectedPaymentType("advance");
      setReceiptFiles({
        advance: null,
        installment: null,
        final: null,
      });
    } catch (err) {
      toast.error("Failed to save reservation!");
      console.error(err);
    } finally {
      setFullLoading(false);
    }
  };

  return (
    <div>
      <ToastContainer />
      <Loader loading={fullLoading || loadingRooms} />
      

      <form onSubmit={handleSubmit}>
        {/* ── Guest Details ── */}
        <div className="card mb-4">
          <div className="card-header py-2">
            <h5 className="mb-0">Guest Details</h5>
          </div>
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label className="form-label">Guest Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  name="guest"
                  value={formData.guest}
                  onChange={handleChange}
                  disabled={isEditingReservation}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Mobile <span className="text-danger">*</span></label>
                <div className="input-group">
                  <span className="input-group-text">03</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    className="form-control"
                    name="mobile"
                    value={(formData.mobile || "03").startsWith("03")
                      ? (formData.mobile || "03").slice(2)
                      : formData.mobile || ""}
                    maxLength={9}
                    placeholder="XXXXXXXXX"
                    onChange={(e) => {
                      const rest = e.target.value.replace(/\D/g, "").slice(0, 9);
                      setFormData((prev) => ({ ...prev, mobile: `03${rest}` }));
                    }}
                    disabled={isEditingReservation}
                    required
                  />
                </div>
                <small className="text-muted">11 digits (03 + 9)</small>
              </div>
              <div className="col-md-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isEditingReservation}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Passport / CNIC <span className="text-danger">*</span></label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  name="passport"
                  value={formData.passport}
                  onChange={handleChange}
                  maxLength={13}
                  placeholder="13-digit CNIC"
                  disabled={isEditingReservation}
                  required
                />
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label className="form-label">Adults <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control"
                  name="adults"
                  value={formData.adults}
                  min={1}
                  onChange={handleChange}
                  disabled={isEditingReservation}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Children <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control"
                  name="children"
                  value={formData.children}
                  min={0}
                  onChange={handleChange}
                  disabled={isEditingReservation}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Infants <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control"
                  name="infants"
                  value={formData.infants}
                  min={0}
                  onChange={handleChange}
                  disabled={isEditingReservation}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Pets <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control"
                  name="pets"
                  value={formData.pets}
                  min={0}
                  onChange={handleChange}
                  disabled={isEditingReservation}
                  required
                />
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-control"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={isEditingReservation}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Booking Details ── */}
        <div className="card mb-4">
          <div className="card-header py-2">
            <h5 className="mb-0">Booking Details</h5>
          </div>
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label">Booking Status <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  name="reservationType"
                  value={formData.reservationType}
                  onChange={handleChange}
                >
                  <option value="Reservation">Reservation</option>
                  <option value="Check-in">Check-in</option>
                  <option value="Check-out">Check-out</option>
                  <option value="Cancelled">Cancelled (hide)</option>
                  <option value="No-show">No-show</option>
                  <option value="Unconfirmed Reservation">
                    Unconfirmed Reservation
                  </option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Booking Source <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  name="bookingSource"
                  value={formData.bookingSource}
                  onChange={handleChange}
                >
                  {BOOKING_SOURCE_OPTIONS.map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.disabled}
                    >
                      {opt.label}
                    </option>
                  ))}
                  {!BOOKING_SOURCE_OPTIONS.some(
                    (opt) => opt.value === formData.bookingSource
                  ) && (
                    <option value={formData.bookingSource} disabled>
                      {formData.bookingSource}
                    </option>
                  )}
                </select>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label">Check-in <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  name="checkIn"
                  value={formData.checkIn}
                  min={checkInMin}
                  onChange={handleChange}
                  disabled={isEditingReservation}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Check-out <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  name="checkOut"
                  value={formData.checkOut}
                  min={checkOutMin}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">No of Days</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.noOfDays}
                  readOnly
                />
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Hotel <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  name="hotelId"
                  value={formData.hotelId}
                  onChange={handleChange}
                  disabled={!formData.checkIn || !formData.checkOut}
                  required
                >
                  <option value="">-- Select Hotel --</option>
                  {hotelOptions.map((h) => (
                    <option key={h.value} value={h.value}>
                      {h.label}
                    </option>
                  ))}
                </select>
                {(!formData.checkIn || !formData.checkOut) && (
                  <small className="text-muted">
                    Select check-in and check-out first
                  </small>
                )}
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isComplimentary"
                    checked={Boolean(formData.isComplimentary)}
                    onChange={handleComplimentaryChange}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="isComplimentary"
                  >
                    Complimentary reservation
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Rooms ── */}
        <div className="card mb-4">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Rooms</h5>
          </div>
          <div className="card-body">
            {formData.hotelId && formData.checkIn && formData.checkOut ? (
              <>
                {formData.rooms.map((room, idx) => (
                  <div key={idx} className="p-3 mb-3 border rounded">
                    <div className="row g-2 mb-2 align-items-end">
                      <div className="col-md-2">
                        <label className="form-label">Room Type <span className="text-danger">*</span></label>
                        <select
                          className="form-select"
                          value={room.roomId}
                          onChange={(e) =>
                            handleRoomChange(idx, "roomId", e.target.value)
                          }
                        >
                          <option value="">-- Select --</option>
                          {roomOptions.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-2">
                        <label className="form-label">Rate Plan <span className="text-danger">*</span></label>
                        <select
                          className="form-select"
                          value={room.ratePlanId || ""}
                          onChange={(e) =>
                            handlePlanSelect(idx, e.target.value)
                          }
                          disabled={!room.availablePlans?.length}
                        >
                          <option value="">-- Select Plan --</option>
                          {room.availablePlans?.map((plan) => (
                            <option key={plan.id} value={plan.id}>
                              {plan.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-2">
                        <label className="form-label">Rate/Night</label>
                        <input
                          type="number"
                          className="form-control"
                          value={room.rate || 0}
                          readOnly
                        />
                      </div>

                      <div className="col-md-2">
                        <label className="form-label">Charge Rate</label>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          placeholder={room.rate || "0"}
                          value={
                            formData.isComplimentary
                              ? 0
                              : room.chargeRate !== "" && room.chargeRate != null
                                ? room.chargeRate
                                : room.rate || ""
                          }
                          onChange={(e) =>
                            handleRoomChange(idx, "chargeRate", e.target.value)
                          }
                          disabled={formData.isComplimentary}
                          readOnly={formData.isComplimentary}
                        />
                      </div>

                      <div className="col-md-2">
                        <label className="form-label">Tax</label>
                        <input
                          type="number"
                          className="form-control"
                          value={room.tax || 0}
                          readOnly
                        />
                      </div>

                      {/* A reservation always keeps at least one room row. */}
                      {formData.rooms.length > 1 && (
                        <div className="col-md-1 d-flex align-items-end pb-1">
                          <button
                            type="button"
                            className="btn btn-danger w-100"
                            onClick={() => handleDeleteRoom(idx)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </div>

                    {room.ratePlanId && (
                      <div className="small text-muted mt-1">
                        {(() => {
                          const plan = room.availablePlans?.find(
                            (p) => p.id === room.ratePlanId
                          );
                          const available = getMaxQuantityForRoom(
                            formData.rooms,
                            idx
                          );
                          return (
                            <>
                              Available rooms: {available ?? "-"}
                              {plan?.totalPlan != null
                                ? ` (Total Plan: ${plan.totalPlan})`
                                : ""}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {room.roomId && (
                      <div className="row mt-3 g-2">
                        <div className="col-md-3">
                          <label className="form-label">
                            Physical Room{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select"
                            value={room.assignedPhysicalRooms?.[0] || ""}
                            onChange={(e) =>
                              handleRoomChange(idx, "assignedPhysicalRoom", {
                                slotIdx: 0,
                                physicalRoomId: e.target.value,
                              })
                            }
                            required
                          >
                            <option value="">-- Select Room --</option>
                            {getPhysicalRoomOptions(
                              room.roomId,
                              formData.rooms,
                              idx,
                              0
                            ).map((pr) => (
                              <option key={pr.id} value={pr.id}>
                                {pr.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {!(getPhysicalRoomOptions(
                          room.roomId,
                          formData.rooms,
                          idx,
                          0
                        ).length) &&
                          !(getCachedPhysicalRooms(room.roomId) || []).length && (
                          <div className="col-12">
                            <small className="text-warning">
                              No physical rooms found for this room type. Add
                              them under Hotel Rooms first.
                            </small>
                          </div>
                        )}
                        {getCachedPhysicalRooms(room.roomId).length > 0 &&
                          !getPhysicalRoomOptions(
                            room.roomId,
                            formData.rooms,
                            idx,
                            0
                          ).length && (
                          <div className="col-12">
                            <small className="text-warning">
                              No available physical rooms left for this hotel /
                              room type.
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleAddRoom}
                  disabled={!roomOptions.length}
                >
                  + Add Room
                </button>
                {!roomOptions.length && (
                  <p className="text-muted mt-2 mb-0">
                    No rooms available for the selected dates.
                  </p>
                )}
              </>
            ) : (
              <p className="text-muted mb-0">
                Select hotel and dates to load rooms.
              </p>
            )}

            <div className="row g-3 mt-3">
              <div className="col-md-6">
                <label className="form-label">Total Rate</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.totalRate}
                  readOnly
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Total Rate (with tax)</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.totalRateWithTax}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Payments ── */}
        <div className="card mb-4">
          <div className="card-header py-2">
            <h5 className="mb-0">Payments</h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Payment Type</label>
                <select
                  className="form-select"
                  value={selectedPaymentType}
                  onChange={(e) => setSelectedPaymentType(e.target.value)}
                >
                  {(reservation
                    ? PAYMENT_TYPE_OPTIONS
                    : PAYMENT_TYPE_OPTIONS.filter((o) => o.value === "advance")
                  ).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <PaymentEntryFields
              entry={payments[selectedPaymentType]}
              existingReceiptUrl={payments[selectedPaymentType].receiptUrl}
              disabled={isEditingReservation}
              onChange={(field, value) =>
                handlePaymentChange(selectedPaymentType, field, value)
              }
              onReceiptChange={(file) =>
                handleReceiptChange(selectedPaymentType, file)
              }
            />
          </div>
        </div>

        {/* ── Booking Notes ── */}
        <div className="card mb-4">
          <div className="card-header py-2">
            <h5 className="mb-0">Booking Notes</h5>
          </div>
          <div className="card-body">
            <label className="form-label">Booking Notes</label>
            <textarea
              className="form-control"
              name="bookingNotes"
              rows={4}
              placeholder="Add any notes for this booking..."
              value={formData.bookingNotes}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="d-flex gap-2 mb-3">
          <button type="submit" className="btn btn-primary">
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={toggleForm}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddReservation;
